import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import LoadingWrapper from 'components/LoadingWrapper';
import {
  fetchObjectMapping,
  fetchObjectRelationships,
  mappingObjectTypesSelector,
  objectRelationshipsLoading,
  objectRelationshipsSelector,
  saveRelationships,
} from 'slices/objectRelationships.slice';
import { useAppDispatch } from 'slices/store';
import { EAppId, type IObjectRelationship } from '@timeedit/types';
import { Alert, DefaultContentLayout, Icon } from '@timeedit/ui-components';
import {
  Button,
  Col,
  Row,
  Select,
  Typography,
} from '@timeedit/ui-components/antd';
import intl from '../../../i18n/intl';

const language = intl.messages;
const { Title } = Typography;

type Props = {
  appName?: EAppId;
  title?: string;
};

const Relations = ({
  appName = EAppId.TE_EXAM,
  title = language.application_exam_setting_title as string,
}: Props) => {
  const dispatch = useAppDispatch();
  const mappingObjectTypes = useSelector(mappingObjectTypesSelector);
  const relationships = useSelector(objectRelationshipsSelector);
  const loading = useSelector(objectRelationshipsLoading);
  const [relations, setRelations] = useState<IObjectRelationship[]>([]);

  useEffect(() => {
    dispatch(fetchObjectRelationships(appName));
    dispatch(fetchObjectMapping(appName));
  }, []);

  useEffect(() => {
    setRelations(relationships);
  }, [relationships]);

  const relationMap = useMemo(() => {
    const _relationMap: { [key: string]: any } = {};
    relations.forEach(({ from, to }) => {
      if (from && to) {
        if (!_relationMap[from]) _relationMap[from] = [];
        if (!_relationMap[to]) _relationMap[to] = [];

        _relationMap[from].push(to);
        _relationMap[to].push(from);
      }
    });
    return _relationMap;
  }, [relations]);

  const typeOptions = useMemo(
    () =>
      mappingObjectTypes.map((type: any) => ({
        label: type.applicationObjectTypeLabel,
        value: type.applicationObjectTypeName,
      })),
    [mappingObjectTypes]
  );

  const saveRelations = useCallback(() => {
    dispatch(saveRelationships({ appId: appName, relationships: relations }));
  }, [relations]);

  const addRelation = useCallback(
    () =>
      setRelations((prevRelations) => [...prevRelations, { from: '', to: '' }]),
    []
  );

  const removeRelation = useCallback(
    (index: number) =>
      setRelations((prevRelations) => {
        const _prevRelations = [...prevRelations];
        _prevRelations.splice(index, 1);
        return _prevRelations;
      }),
    []
  );

  const setRelation = useCallback(
    (value: string, prop: string, index: number) => {
      setRelations((prevRelations) => {
        const newRelations = [...prevRelations];
        newRelations[index] = {
          ...newRelations[index],
          [prop]: value,
        };
        return newRelations;
      });
    },
    []
  );

  return (
    <DefaultContentLayout
      title={title}
      subtitle={
        language.application_exam_object_relationship_subtitle as string
      }
      actions={
        <Button type="primary" onClick={saveRelations}>
          {language.save_changes as string}
        </Button>
      }
    >
      <div style={{ padding: '30px 20px' }}>
        <LoadingWrapper isLoading={loading}>
          <Alert
            type="info"
            title="Please make sure to only map relations that is configured in TE server"
            showIcon
          />
          <Row gutter={10} style={{ marginTop: 10 }}>
            <Col span={4}>
              <Title level={4}>Select type:</Title>
            </Col>
            <Col span={1} />
            <Col span={4}>
              <Title level={4}>Relation to:</Title>
            </Col>
          </Row>
          {relations.map(({ from, to }, index) => (
            <Row gutter={10} key={index} style={{ marginTop: 10 }}>
              <Col span={4}>
                <Select
                  value={from}
                  placeholder="Please select"
                  onChange={(value) => setRelation(value, 'from', index)}
                  style={{ width: '100%' }}
                >
                  {typeOptions.map(({ value, label }) => (
                    <Select.Option
                      key={value}
                      value={value}
                      disabled={
                        to &&
                        (value === to ||
                          (relationMap[to] && relationMap[to].includes(value)))
                      }
                    >
                      {label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={1}>
                <Icon
                  id="SwapOutlined"
                  alt="Swap"
                  style={{
                    lineHeight: '32px',
                    position: 'relative',
                    left: '35%',
                  }}
                />
              </Col>
              <Col span={4}>
                <Select
                  value={to}
                  placeholder="Please select"
                  onChange={(value) => setRelation(value, 'to', index)}
                  style={{ width: '100%' }}
                >
                  {typeOptions.map(({ value, label }) => (
                    <Select.Option
                      key={value}
                      value={value}
                      disabled={
                        from &&
                        (value === from ||
                          (relationMap[from] &&
                            relationMap[from].includes(value)))
                      }
                    >
                      {label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={1}>
                <Button
                  onClick={() => removeRelation(index)}
                  style={{ border: 0, position: 'relative' }}
                >
                  <Icon
                    id="MinusSquareOutlined"
                    alt="Minus Square"
                    style={{ color: 'red', fontSize: 16, lineHeight: '32px' }}
                  />
                </Button>
              </Col>
            </Row>
          ))}
          <Row style={{ marginTop: 10 }}>
            <Col>
              <Button onClick={addRelation} style={{ border: 0 }}>
                <Icon id="PlusSquareOutlined" alt="Plus Square" />
                Add relation
              </Button>
            </Col>
          </Row>
        </LoadingWrapper>
      </div>
    </DefaultContentLayout>
  );
};

export default Relations;
